/**
 * Token domain types and Zod schemas
 *
 * ARCH-30: Token metadata stored in D1 (24 columns).
 * IDs use branded types for compile-time safety.
 */

import { z } from "zod";

import type { Brand } from "./domain/branded";

// ─── Branded ID ──────────────────────────────────────────────────
export type TokenId = Brand<string, "TokenId">;

export function tokenId(id: string): TokenId {
	return id as TokenId;
}

export function isTokenId(value: unknown): value is TokenId {
	return typeof value === "string" && value.length > 0;
}

// ─── Enums ───────────────────────────────────────────────────────
export const TokenCategorySchema = z.enum([
	"AI",
	"DeFi",
	"Gaming",
	"Creator",
	"Meme",
	"Social",
	"Infrastructure",
	"Other",
]);
export type TokenCategory = z.infer<typeof TokenCategorySchema>;

export const VerificationStatusSchema = z.enum(["unverified", "verified", "flagged", "removed"]);
export type VerificationStatus = z.infer<typeof VerificationStatusSchema>;

export const TokenStatusSchema = z.enum(["active", "paused", "inactive"]);
export type TokenStatus = z.infer<typeof TokenStatusSchema>;

// ─── Token Schema ────────────────────────────────────────────────
export const TokenSchema = z.object({
	id: z.string(),
	name: z.string(),
	symbol: z.string(),
	description: z.string().nullable(),
	tagline: z.string().nullable(),
	category: TokenCategorySchema,
	logoUploadId: z.string().nullable(),
	creatorWallet: z.string(),
	contractAddress: z.string().nullable(),
	txHash: z.string().nullable(),
	feeSplitCreator: z.number().int(),
	feeSplitTreasury: z.number().int(),
	feeSplitProtocol: z.number().int(),
	hookType: z.string().nullable(),
	mevModule: z.string().nullable(),
	extensions: z.string().nullable(),
	chainId: z.number().int(),
	poolAddress: z.string().nullable(),
	status: TokenStatusSchema,
	verificationStatus: VerificationStatusSchema,
	verificationReason: z.string().nullable(),
	verifiedBy: z.string().nullable(),
	verifiedAt: z.string().nullable(),
	createdAt: z.string(),
	updatedAt: z.string(),
});

export type Token = z.infer<typeof TokenSchema>;

// ─── Create Token Schema (input validation) ─────────────────────
export const CreateTokenSchema = z
	.object({
		name: z.string().min(1).max(100),
		symbol: z.string().min(1).max(10).toUpperCase(),
		description: z.string().max(1000).optional(),
		tagline: z.string().max(200).optional(),
		category: TokenCategorySchema,
		logoUploadId: z.string().optional(),
		feeSplitCreator: z.number().int().min(0).max(100).default(0),
		feeSplitTreasury: z.number().int().min(0).max(100).default(0),
		feeSplitProtocol: z.number().int().min(0).max(100).default(0),
		extensions: z.string().optional(),
		chainId: z.number().int(),
	})
	.refine((data) => data.feeSplitCreator + data.feeSplitTreasury + data.feeSplitProtocol <= 100, {
		error: "Fee splits must sum to 100 or less",
		path: ["feeSplitCreator"],
	});

export type CreateToken = z.infer<typeof CreateTokenSchema>;

// ─── Social Links Schema ─────────────────────────────────────────
export const SocialLinksSchema = z
	.object({
		website: z.string().url().optional(),
		twitter: z.string().url().optional(),
		discord: z.string().url().optional(),
		telegram: z.string().url().optional(),
	})
	.optional();

export type SocialLinks = z.infer<typeof SocialLinksSchema>;

// ─── Create Token Details Schema (Step 1 of wizard) ─────────────
export const CreateTokenDetailsSchema = z.object({
	name: z.string().min(1, "Name is required").max(50, "Name must be 50 characters or less"),
	symbol: z
		.string()
		.min(1, "Symbol is required")
		.max(10, "Symbol must be 10 characters or less")
		.transform((val) => val.toUpperCase())
		.pipe(
			z.string().regex(/^[A-Z0-9]+$/, "Symbol must contain only uppercase letters and numbers")
		),
	tagline: z.string().max(100, "Tagline must be 100 characters or less").optional(),
	description: z
		.string()
		.min(1, "Description is required")
		.max(500, "Description must be 500 characters or less"),
	category: TokenCategorySchema,
	logoUploadId: z.string().optional(),
	socialLinks: SocialLinksSchema,
});

export type CreateTokenDetailsFormData = z.infer<typeof CreateTokenDetailsSchema>;

// ─── Register Token Schema (Story 2.4) ──────────────────────────
const TokenWalletAddressSchema = z.string().regex(/^0x[a-fA-F0-9]{40}$/, "Invalid wallet address");

export const RegisterTokenRequestSchema = z
	.object({
		// Token metadata
		name: z.string().min(1, "Name is required").max(50, "Name must be 50 characters or less"),
		symbol: z
			.string()
			.min(1, "Symbol is required")
			.max(10, "Symbol must be 10 characters or less")
			.transform((s) => s.toUpperCase()),
		description: z.string().max(1000, "Description must be 1000 characters or less").optional(),
		tagline: z.string().max(200, "Tagline must be 200 characters or less").optional(),
		category: TokenCategorySchema,
		logoUploadId: z.string().optional(), // UploadId - will be validated in handler
		socialLinks: z.string().optional(), // JSON string

		// Deployment details
		contractAddress: TokenWalletAddressSchema,
		poolAddress: TokenWalletAddressSchema.optional(),
		txHash: z.string().regex(/^0x[a-fA-F0-9]{64}$/, "Invalid transaction hash"),
		chainId: z.number().int().positive(),

		// V4 configuration
		feeSplitCreator: z.number().int().min(0).max(100),
		feeSplitTreasury: z.number().int().min(0).max(100),
		feeSplitProtocol: z.number().int().min(0).max(100),
		extensions: z.string().optional(), // JSON string
	})
	.refine((data) => data.feeSplitCreator + data.feeSplitTreasury + data.feeSplitProtocol <= 100, {
		message: "Fee splits must sum to 100 or less",
		path: ["feeSplitCreator"],
	});

export type RegisterTokenRequest = z.infer<typeof RegisterTokenRequestSchema>;

// ─── Token Response Schema ──────────────────────────────────────
export const TokenResponseSchema = z.object({
	id: z.string(),
	name: z.string(),
	symbol: z.string(),
	description: z.string().nullable(),
	tagline: z.string().nullable(),
	category: TokenCategorySchema,
	logoUploadId: z.string().nullable(),
	logoUrl: z.string().nullable(),
	socialLinks: z.string().nullable(),
	creatorWallet: z.string(),
	contractAddress: z.string().nullable(),
	poolAddress: z.string().nullable(),
	txHash: z.string().nullable(),
	chainId: z.number(),
	feeSplitCreator: z.number(),
	feeSplitTreasury: z.number(),
	feeSplitProtocol: z.number(),
	hookType: z.string().nullable(),
	mevModule: z.string().nullable(),
	extensions: z.string().nullable(),
	status: TokenStatusSchema,
	verificationStatus: VerificationStatusSchema,
	verificationReason: z.string().nullable(),
	verifiedBy: z.string().nullable(),
	verifiedAt: z.string().nullable(),
	createdAt: z.string(),
	updatedAt: z.string(),
	dataSource: z.object({
		source: z.literal("database"),
		timestamp: z.string(),
	}),
});

export type TokenResponse = z.infer<typeof TokenResponseSchema>;
